import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Mail, User } from 'lucide-react';
import { api } from '@/lib/api';
import { usePageMeta } from '@/lib/usePageMeta';

const CATEGORY_IDS = [
  'business_systems',
  'founder_dependency',
  'customer_journey',
  'operations',
  'marketing_infrastructure',
  'ai_automation_readiness',
  'data_visibility',
];

const CATEGORY_SUMMARY_LABEL = {
  business_systems: 'Systems',
  founder_dependency: 'Founder Dependency',
  customer_journey: 'Customer Journey',
  operations: 'Operations',
  marketing_infrastructure: 'Marketing',
  ai_automation_readiness: 'AI + Automation',
  data_visibility: 'Business Visibility',
};

const PHASES = { LANDING: 'landing', ASSESSMENT: 'assessment', GATE: 'gate', RESULTS: 'results' };

export default function FounderScorecard() {
  usePageMeta({
    title: 'Founder Systems Scorecard™ | Toia Wallace',
    description:
      'In less than 7 minutes, discover where your business is strong, where it still depends on you, and where systems, AI, or automation could create more capacity.',
    path: '/founder-scorecard',
    ogTitle: 'Founder Systems Scorecard™ | Toia Wallace',
  });

  const [phase, setPhase] = useState(PHASES.LANDING);
  const [spec, setSpec] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { "cat_id:qidx": 0..4 }
  const [first_name, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Fetch spec once when entering assessment
  useEffect(() => {
    if (phase === PHASES.ASSESSMENT && !spec) {
      api.get('/scorecard/spec').then((r) => setSpec(r.data)).catch(() => setError('Could not load the scorecard. Please refresh.'));
    }
  }, [phase, spec]);

  const totalSteps = spec?.categories?.length || 7;
  const currentCategory = spec?.categories?.[stepIndex];
  const progressPct = Math.round(((stepIndex) / totalSteps) * 100);

  const answerLabels = (cat) => (cat?.reverse ? spec.answer_labels_reverse : spec.answer_labels_standard);

  const currentStepComplete = useMemo(() => {
    if (!currentCategory) return false;
    return currentCategory.questions.every((_, qIdx) => answers[`${currentCategory.id}:${qIdx}`] != null);
  }, [currentCategory, answers]);

  const start = () => {
    setPhase(PHASES.ASSESSMENT);
    setStepIndex(0);
    window.scrollTo(0, 0);
  };

  const next = () => {
    if (!currentStepComplete) return;
    if (stepIndex + 1 >= totalSteps) {
      setPhase(PHASES.GATE);
      window.scrollTo(0, 0);
    } else {
      setStepIndex(stepIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const back = () => {
    if (stepIndex === 0) {
      setPhase(PHASES.LANDING);
    } else {
      setStepIndex(stepIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!first_name.trim() || !email.trim()) {
      setError('Please share your first name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: first_name.trim(),
        email: email.trim(),
        consent_given: consent,
        source: 'founder_scorecard',
        website,
        answers: CATEGORY_IDS.flatMap((cid) =>
          [0, 1, 2, 3].map((q) => ({ category_id: cid, question_index: q, value: answers[`${cid}:${q}`] })),
        ),
      };
      const { data } = await api.post('/scorecard/submit', payload);
      setResults(data);
      setPhase(PHASES.RESULTS);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#FAF8F5] text-[#111]">
      {phase === PHASES.LANDING && <Landing onStart={start} />}
      {phase === PHASES.ASSESSMENT && (
        <Assessment
          spec={spec}
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          currentCategory={currentCategory}
          progressPct={progressPct}
          answers={answers}
          setAnswers={setAnswers}
          answerLabels={answerLabels}
          currentStepComplete={currentStepComplete}
          back={back}
          next={next}
          error={error}
        />
      )}
      {phase === PHASES.GATE && (
        <EmailGate
          first_name={first_name}
          setFirstName={setFirstName}
          email={email}
          setEmail={setEmail}
          consent={consent}
          setConsent={setConsent}
          website={website}
          setWebsite={setWebsite}
          submitting={submitting}
          onBack={() => { setPhase(PHASES.ASSESSMENT); setStepIndex(totalSteps - 1); }}
          onSubmit={submit}
          error={error}
        />
      )}
      {phase === PHASES.RESULTS && results && <Results data={results} />}
    </div>
  );
}

/* ---------- Landing ---------- */
function Landing({ onStart }) {
  return (
    <>
      {/* Hero */}
      <section className="px-5 sm:px-10 lg:px-20 pt-16 sm:pt-24 pb-12 sm:pb-20">
        <div className="max-w-5xl">
          <div className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#888] mb-5" data-testid="scorecard-eyebrow">
            Founder Systems Scorecard<span className="align-super text-[8px]">™</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-[-0.02em] text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            How strong are the systems <em className="italic text-[#C8A15A]">underneath</em> your business?
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#333] max-w-2xl leading-relaxed">
            Take the Founder Systems Scorecard<span className="align-super text-xs">™</span> and discover what your business needs to build next.
          </p>
          <p className="mt-6 font-serif italic text-xl sm:text-2xl text-[#555] max-w-3xl leading-snug" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            “In less than 7 minutes, you’ll see where your business is strong, where it still depends too heavily on you, and where systems, AI, or automation could create more capacity.”
          </p>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
            <button
              type="button"
              onClick={onStart}
              data-testid="scorecard-start-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#111] text-[#FAF8F5] px-8 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-[#C8A15A] hover:text-[#111] transition-all"
            >
              Take the Free Scorecard
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="text-xs tracking-[0.18em] uppercase text-[#888]">28 Questions · Free · Immediate Results</div>
          </div>
        </div>
      </section>

      {/* Section 2 */}
      <section className="px-5 sm:px-10 lg:px-20 py-16 sm:py-24 bg-white border-t border-[#eae4d8]">
        <div className="max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-4">The Problem</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-[-0.01em] text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Growth gets harder when the founder is still <em className="italic text-[#C8A15A]">the system.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#333] text-base sm:text-[17px] leading-relaxed">
            <p>Your business may be growing.</p>
            <p>But are the systems underneath it growing too?</p>
            <p>The Founder Systems Scorecard evaluates seven areas that affect your ability to grow without creating more chaos:</p>
            <div className="pt-2 flex flex-wrap gap-2">
              {['Systems', 'Founder Dependency', 'Customer Journey', 'Operations', 'Marketing', 'AI + Automation', 'Business Visibility'].map((t) => (
                <span key={t} className="inline-flex items-center text-[11px] tracking-[0.14em] uppercase text-[#111] bg-[#FAF8F5] border border-[#eae4d8] px-3 py-1.5 rounded-full">{t}</span>
              ))}
            </div>
            <p>You’ll receive a personalized Founder Systems Score and a clear recommendation for what to strengthen next.</p>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section className="px-5 sm:px-10 lg:px-20 py-16 sm:py-24">
        <div className="max-w-5xl">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#888] mb-6">What You’ll Discover</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-[-0.01em] text-[#111] mb-10 max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            You’ll discover:
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 max-w-3xl text-[#333] text-base sm:text-[17px]">
            {[
              'Your overall Founder Systems Score',
              'Your strongest area',
              'Your weakest area',
              'Where your business still depends on you',
              'Your next systems priority',
              'Where AI or automation could create leverage',
              'The type of infrastructure your current business stage needs',
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#C8A15A] flex-shrink-0"></span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 4 */}
      <section className="px-5 sm:px-10 lg:px-20 py-16 sm:py-24 bg-[#111] text-[#FAF8F5]">
        <div className="max-w-5xl">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-6">Built For Founders</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-[-0.01em]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Built for founders who are tired of running everything manually.
          </h2>
          <div className="mt-8 space-y-5 max-w-3xl text-[#d8d3cb] text-base sm:text-[17px] leading-relaxed">
            <p>This isn’t another generic business checklist.</p>
            <p>I built this Scorecard around the same questions I use when thinking about companies, technology, workflows, AI, and operating systems.</p>
            <p>Because sometimes the problem isn’t that your business needs more.</p>
            <p className="font-serif italic text-xl text-[#FAF8F5]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Sometimes it needs to operate better.</p>
          </div>
          <div className="mt-10">
            <button
              type="button"
              onClick={onStart}
              data-testid="scorecard-start-btn-bottom"
              className="inline-flex items-center gap-3 bg-[#C8A15A] text-[#111] px-8 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-[#FAF8F5] transition-all"
            >
              Find Out What To Build Next
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

/* ---------- Assessment Step ---------- */
function Assessment({ spec, stepIndex, totalSteps, currentCategory, progressPct, answers, setAnswers, answerLabels, currentStepComplete, back, next, error }) {
  if (!spec) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8A15A]" />
      </div>
    );
  }
  if (error) {
    return <div className="px-6 py-24 text-center text-red-600">{error}</div>;
  }
  const labels = answerLabels(currentCategory);
  return (
    <section className="px-5 sm:px-10 lg:px-20 py-10 sm:py-16">
      <div className="max-w-3xl mx-auto">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[#888] mb-3">
            <span data-testid="scorecard-step-label">Category {stepIndex + 1} of {totalSteps}</span>
            <span>{Math.round(((stepIndex + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="h-[2px] bg-[#eae4d8] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#C8A15A] transition-all"
              style={{ width: `${Math.round(((stepIndex + 1) / totalSteps) * 100)}%` }}
            />
          </div>
        </div>

        <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-4" data-testid="scorecard-category-label">
          Category {currentCategory.number} · {currentCategory.name}
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.01em] text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {currentCategory.name}
        </h2>
        {currentCategory.reverse && (
          <p className="mt-3 text-xs tracking-wide text-[#888] italic">Choose the answer that best reflects your business today.</p>
        )}

        <div className="mt-10 space-y-10 sm:space-y-12">
          {currentCategory.questions.map((q, qIdx) => {
            const key = `${currentCategory.id}:${qIdx}`;
            const selected = answers[key]; // undefined until the user picks — no default
            const groupName = `scorecard-${currentCategory.id}-q${qIdx}`;
            return (
              <fieldset key={key} data-testid={`scorecard-question-${currentCategory.id}-${qIdx}`} className="border-0 p-0 m-0">
                <legend className="sr-only">{q}</legend>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">Question {qIdx + 1}</div>
                <p className="font-serif text-xl sm:text-2xl leading-snug text-[#111] mb-5" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {q}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-3" role="radiogroup" aria-label={q}>
                  {labels.map((label, i) => {
                    const isActive = selected === i;
                    const inputId = `${groupName}-opt-${i}`;
                    return (
                      <label
                        key={i}
                        htmlFor={inputId}
                        data-testid={`scorecard-answer-${currentCategory.id}-${qIdx}-${i}`}
                        data-selected={isActive ? 'true' : 'false'}
                        className={`relative cursor-pointer select-none block px-4 py-3 border rounded-xl transition-all text-sm ${
                          isActive
                            ? 'bg-[#FBF4E4] border-[#C8A15A] text-[#111] shadow-[0_0_0_1px_#C8A15A_inset]'
                            : 'bg-white border-[#eae4d8] text-[#333] hover:border-[#C8A15A]/60'
                        }`}
                      >
                        <input
                          id={inputId}
                          type="radio"
                          name={groupName}
                          value={i}
                          checked={isActive}
                          onChange={() => setAnswers((prev) => ({ ...prev, [key]: i }))}
                          className="sr-only"
                          data-testid={`scorecard-input-${currentCategory.id}-${qIdx}-${i}`}
                        />
                        <div className="flex items-start gap-3">
                          <span
                            aria-hidden="true"
                            className={`mt-[3px] inline-flex items-center justify-center w-4 h-4 rounded-full border transition-all flex-shrink-0 ${
                              isActive ? 'border-[#C8A15A] bg-white' : 'border-[#c9c3b7] bg-white'
                            }`}
                          >
                            {isActive && <span className="w-2 h-2 rounded-full bg-[#C8A15A]" />}
                          </span>
                          <span className="flex-1">
                            <span className={`block text-[10px] tracking-[0.18em] uppercase mb-1 ${isActive ? 'text-[#C8A15A]' : 'text-[#888]'}`}>
                              Option {String.fromCharCode(65 + i)}
                            </span>
                            <span className={`block ${isActive ? 'text-[#111] font-medium' : 'text-[#333]'}`}>{label}</span>
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>

        <div className="mt-14 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            data-testid="scorecard-back-btn"
            className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#888] hover:text-[#111]"
          >
            <ArrowLeft className="w-4 h-4" />
            {stepIndex === 0 ? 'Back to overview' : 'Previous category'}
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!currentStepComplete}
            data-testid="scorecard-next-btn"
            className={`inline-flex items-center gap-3 px-7 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase transition-all ${
              currentStepComplete ? 'bg-[#111] text-[#FAF8F5] hover:bg-[#C8A15A] hover:text-[#111]' : 'bg-[#eae4d8] text-[#888] cursor-not-allowed'
            }`}
          >
            {stepIndex + 1 === totalSteps ? 'See Results' : 'Continue'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Email Gate ---------- */
function EmailGate({ first_name, setFirstName, email, setEmail, consent, setConsent, website, setWebsite, submitting, onBack, onSubmit, error }) {
  return (
    <section className="px-5 sm:px-10 lg:px-20 py-16 sm:py-24">
      <div className="max-w-xl mx-auto">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-4" data-testid="gate-eyebrow">Final Step</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-[-0.02em] text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Your Founder Systems Score is <em className="italic text-[#C8A15A]">ready.</em>
        </h1>
        <p className="mt-5 text-base text-[#555]">Share where to send your personalized results. You’ll also join The Founder’s List for weekly notes from Toia.</p>
        <form onSubmit={onSubmit} className="mt-10 space-y-5" noValidate>
          {/* Honeypot */}
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1 }} />
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">First Name</label>
            <div className="relative">
              <User className="w-4 h-4 text-[#888] absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={first_name}
                onChange={(e) => setFirstName(e.target.value)}
                required
                data-testid="gate-first-name"
                className="w-full pl-11 pr-4 py-3.5 bg-white border border-[#eae4d8] rounded-xl focus:border-[#C8A15A] focus:outline-none text-[#111]"
                placeholder="Jordan"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[#888] mb-2">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-[#888] absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="gate-email"
                className="w-full pl-11 pr-4 py-3.5 bg-white border border-[#eae4d8] rounded-xl focus:border-[#C8A15A] focus:outline-none text-[#111]"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <label className="flex items-start gap-3 text-xs text-[#555] leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="gate-consent"
              className="mt-0.5 w-4 h-4 accent-[#C8A15A]"
            />
            <span>By submitting, you agree to receive your Scorecard results and emails from Toia Wallace. You may unsubscribe at any time.</span>
          </label>
          {error && <div className="text-sm text-red-600" data-testid="gate-error">{error}</div>}
          <div className="pt-4 flex items-center justify-between">
            <button type="button" onClick={onBack} className="text-xs tracking-[0.18em] uppercase text-[#888] hover:text-[#111]">
              ← Review Answers
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="gate-submit-btn"
              className="inline-flex items-center gap-3 bg-[#111] text-[#FAF8F5] px-7 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-[#C8A15A] hover:text-[#111] transition-all disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Scoring…</> : <>Show Me My Results <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

/* ---------- Results ---------- */
function Results({ data }) {
  const first = (data.first_name || 'there').split(' ')[0];
  return (
    <section className="px-5 sm:px-10 lg:px-20 py-16 sm:py-24">
      <div className="max-w-4xl mx-auto">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-4" data-testid="results-eyebrow">Founder Systems Scorecard<span className="align-super text-[8px]">™</span></div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Hi {first} — here are your results.
        </h1>

        {/* Score card */}
        <div className="mt-10 bg-white border border-[#eae4d8] rounded-2xl p-8 sm:p-12 text-center">
          <div className="font-serif text-[72px] sm:text-[108px] leading-none text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="results-score">
            {data.overall_score}<span className="text-[#C8A15A] text-[42px] sm:text-[54px]">/100</span>
          </div>
          <div className="mt-4 text-[10px] tracking-[0.28em] uppercase text-[#888]">Result Stage</div>
          <div className="mt-1 font-serif text-2xl sm:text-3xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="results-stage-name">
            {data.result_stage_name}
          </div>
          <p className="mt-5 text-[#555] text-base max-w-xl mx-auto leading-relaxed">{data.result_stage_summary}</p>
          <div className="mt-6 text-xs tracking-[0.18em] uppercase text-[#C8A15A]">Priority · {data.result_stage_priority}</div>
          <div className="mt-1 text-xs text-[#888]">Start with: {data.result_stage_start_with}</div>
        </div>

        {/* Strongest / Weakest */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ResultCard label="Your strongest area" title={data.strongest.name} pct={data.strongest.percentage} tone="light" testid="results-strongest" />
          <ResultCard label="Your biggest opportunity" title={data.weakest.name} pct={data.weakest.percentage} tone="gold" testid="results-weakest" />
        </div>

        {/* Next system + AI opportunity */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="bg-white border border-[#eae4d8] rounded-2xl p-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#888] mb-3">Your Next System To Build</div>
            <p className="text-[#111] text-[17px] leading-relaxed" data-testid="results-next-system">{data.next_system_to_build}</p>
          </div>
          <div className="bg-[#111] text-[#FAF8F5] rounded-2xl p-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A15A] mb-3">Your AI Opportunity</div>
            <p className="text-[#FAF8F5] text-[17px] leading-relaxed" data-testid="results-ai-opportunity">{data.ai_opportunity}</p>
          </div>
        </div>

        {/* Category breakdown */}
        <div className="mt-12">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#888] mb-5">Full Category Breakdown</div>
          <div className="bg-white border border-[#eae4d8] rounded-2xl overflow-hidden">
            {data.categories.map((c, idx) => (
              <div key={c.id} className={`flex items-center gap-5 px-6 sm:px-8 py-5 ${idx > 0 ? 'border-t border-[#eae4d8]' : ''}`} data-testid={`results-cat-${c.id}`}>
                <div className="w-6 text-[11px] tracking-[0.18em] text-[#888]">0{idx + 1}</div>
                <div className="flex-1">
                  <div className="font-serif text-xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{c.name}</div>
                  <div className="mt-2 h-[3px] bg-[#f1ece3] relative overflow-hidden rounded-full">
                    <div className="absolute inset-y-0 left-0 bg-[#C8A15A] rounded-full" style={{ width: `${c.percentage}%` }} />
                  </div>
                </div>
                <div className="font-serif text-2xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{c.percentage}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA + confirmation */}
        <div className="mt-12 bg-[#FAF8F5] border border-[#eae4d8] rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
          <div>
            <div className="font-serif text-2xl sm:text-3xl text-[#111]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Ready to build your next system?</div>
            <p className="mt-2 text-[#555] text-sm">A 90-minute private strategy session with Toia — walk away with a customized plan.</p>
          </div>
          <Link
            to="/strategy-intensive"
            data-testid="results-cta-strategy"
            className="inline-flex items-center gap-3 bg-[#111] text-[#FAF8F5] px-7 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-[#C8A15A] hover:text-[#111] transition-all whitespace-nowrap"
          >
            Book a Business Strategy Intensive
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 text-center text-sm text-[#888]" data-testid="results-confirmation">
          You’re now on The Founder’s List. Watch your inbox for your full Scorecard breakdown.
        </div>
      </div>
    </section>
  );
}

function ResultCard({ label, title, pct, tone, testid }) {
  const bg = tone === 'gold' ? 'bg-[#C8A15A] text-[#111] border-[#C8A15A]' : 'bg-white text-[#111] border-[#eae4d8]';
  return (
    <div className={`${bg} border rounded-2xl p-8`} data-testid={testid}>
      <div className={`text-[10px] tracking-[0.28em] uppercase mb-3 ${tone === 'gold' ? 'text-[#111]/70' : 'text-[#888]'}`}>{label}</div>
      <div className="flex items-baseline justify-between">
        <div className="font-serif text-2xl sm:text-3xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{title}</div>
        <div className="font-serif text-3xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{pct}%</div>
      </div>
    </div>
  );
}
